import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { alpha, Theme, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";

export interface KpiTileProps {
  label: string;
  value: ReactNode;
  hint?: string;
  /** Shown in a tinted square left of the text. */
  icon?: ReactNode;
  /**
   * Icon colour: a palette key or path such as `"success"` / `"success.main"`, or a CSS colour.
   * Defaults to the theme's primary colour.
   */
  color?: string;
  minWidth?: number;
  /** Makes the tile a toggle button, e.g. to filter a list by what the tile counts. */
  onClick?: () => void;
  /** Marks a clickable tile as active (`aria-pressed`, primary border). */
  selected?: boolean;
}

/** One key figure in a row of summary tiles above a page's table or chart. */
export function KpiTile({
  label,
  value,
  hint,
  icon,
  color,
  minWidth = 180,
  onClick,
  selected = false,
}: KpiTileProps) {
  const theme = useTheme();
  const iconColor = color ? resolvePaletteColor(theme, color) : theme.palette.primary.main;

  const content = (
    <CardContent>
      <Stack direction="row" alignItems="center" gap={1.5}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(iconColor, theme.palette.mode === "dark" ? 0.25 : 0.12),
              color: iconColor,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  );

  if (!onClick) {
    return <Card sx={{ flex: 1, minWidth }}>{content}</Card>;
  }

  return (
    <Card
      sx={{
        flex: 1,
        minWidth,
        border: 2,
        borderColor: selected ? "primary.main" : "transparent",
      }}
    >
      <CardActionArea onClick={onClick} aria-pressed={selected} sx={{ height: "100%" }}>
        {content}
      </CardActionArea>
    </Card>
  );
}

/**
 * `"success.main"` → the palette value, `"success"` → its `main` shade; anything else is taken as
 * a CSS colour.
 */
function resolvePaletteColor(theme: Theme, color: string): string {
  let node: unknown = theme.palette;
  for (const key of color.split(".")) {
    node = node && typeof node === "object" ? (node as Record<string, unknown>)[key] : undefined;
  }
  if (typeof node === "string") return node;
  const main = node && typeof node === "object" ? (node as { main?: unknown }).main : undefined;
  return typeof main === "string" ? main : color;
}

/**
 * The click handler of a tile that filters by what it counts. An unknown count would filter to
 * nothing, read as "none", so it is not clickable; an active filter stays clickable to clear it.
 */
export function filterTileClick(
  count: number | null,
  active: boolean,
  toggle: () => void,
): (() => void) | undefined {
  return count === null && !active ? undefined : toggle;
}
